/**
 * AccountDetails.js
 *
 * This component displays the user's account details, including their email and password change options.
 *
 * Author: Meng Li
 * Created on: 2024-11-07
 * Professor: Mejdi Eraslan
 * Client: Melisa Eraslan
 */

import React, { useState } from 'react';
import UserPanelSidebar from '../components/UserPanelSidebar';
import { getAuth, updatePassword, updateEmail, reauthenticateWithCredential, EmailAuthProvider, sendEmailVerification } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import '../css/AccountDetails.css';

const AccountDetails = () => {
    const auth = getAuth();
    const user = auth.currentUser;
    const [email, setEmail] = useState(user.email);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState(null);

    // Handle email change and send verification email
    const handleEmailChange = async () => {
        try {
            if (email !== user.email) {
                await updateEmail(user, email);
                await sendEmailVerification(user); // Send verification email
                setMessage("A verification email has been sent to your new email address. Please verify it to complete the update.");
            } else {
                setMessage("No changes detected in email.");
            }
        } catch (error) {
            setMessage(error.message);
        }
    };

    // Handle password change
    const handlePasswordChange = async () => {
        if (!currentPassword || !newPassword || !confirmPassword) {
            setMessage("Please fill in all password fields.");
            return;
        }

        if (newPassword !== confirmPassword) {
            setMessage("New passwords do not match.");
            return;
        }

        try {
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential); // Re-authenticate user

            await updatePassword(user, newPassword);
            setMessage("Your password has been updated successfully.");
        } catch (error) {
            setMessage(error.message);
        }
    };

    return (
        <div className="account-details-page">
            <UserPanelSidebar />
            <div className="account-details-container">
                <form>
                    {/* Email Change Section */}
                    <div className="section">
                        <h3 className="section-title">Email change</h3>
                        <label>Email address *</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="input-field"
                        />
                        <button type="button" className="save-btn" onClick={handleEmailChange}>
                            Save Email
                        </button>
                    </div>

                    {/* Password Change Section */}
                    <div className="section">
                        <h3 className="section-title">Password change</h3>
                        <label>Current password (leave blank to leave unchanged)</label>
                        <input
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            className="input-field"
                            placeholder="Enter your current password"
                        />

                        <label>New password (leave blank to leave unchanged)</label>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="input-field"
                        />

                        <label>Confirm new password</label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="input-field"
                        />

                        <button type="button" className="save-btn" onClick={handlePasswordChange}>
                            Save Password
                        </button>
                    </div>
                </form>

                {/* Success/Error Message Modal */}
                {message && (
                    <div className="modal" onClick={() => setMessage(null)}>
                        <div className="modal-content">
                            <p>{message}</p>
                            <button onClick={() => setMessage(null)} className="close-btn">Close</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AccountDetails;
